import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import type { RespondedType, ResponseError } from '@/models/response';
import { makePrompt } from '@/stores/prompter';
import { addToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { tagListQueryOptions, useTagById } from './use-tags';

export default function useUpdateTag(tagId: number) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { data } = useTagById(tagId);
  const { TagName } = data || {};

  const mutation = useMutation<RespondedType, ResponseError, string>({
    mutationFn: (params) => fetcher('/UpdateTag', { TagId: tagId, TagName: params }, { validResponseCodes: [0] }),
    onSuccess: (_, params) => {
      addToast({
        type: 'success',
        message: t('common.rename-success'),
      });

      queryClient.setQueryData(tagListQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (draft?.Data) {
            const index = draft.Data.Tags.findIndex((tag) => tag.Id === tagId);
            if (index !== -1 && draft.Data.Tags[index]) {
              draft.Data.Tags[index].TagName = params;
            }
          }

          return draft;
        }),
      );
    },
  });

  const mutateAsyncWithPrompt = () =>
    makePrompt({
      key: 'update-tag',
      type: 'form',
      title: t('contact.tags.change-name'),
      meta: {
        schema: updateTagFormSchema,
        defaultValues: { tagName: TagName },
      },
      promise: async (resolution) => {
        const result = await updateTagFormSchema.safeParseAsync(resolution);

        if (result.success) {
          return await mutation.mutateAsync(result.data.tagName);
        }
      },
    });

  return {
    ...mutation,
    mutateAsyncWithPrompt,
  };
}

const updateTagFormSchema = z.object({
  tagName: z
    .string()
    .trim()
    .min(1, 'errors.string.min-length|count=1')
    .max(12, 'errors.string.max-length|count=12')
    .describe('contact.tags.tag-name'),
});
